import { PropsWithChildren, useMemo } from "react";

import { ExternalLink } from "@aircall/react-icons";
import { differenceInSeconds } from "date-fns";
import { useTranslation } from "react-i18next";

import { Duration } from "@/blocks/Duration";
import { Section, SectionHeader, SectionTitle } from "@/blocks/Section";

import i18n from "@/config/i18n.config";
import { DASHBOARD_URL } from "@/constants/environment.constants";
import {
  getRelativeDate,
  longPrettifiedDate,
} from "@/helpers/date/date.helpers";
import { useCall } from "@/hooks/useCall/useCall";
import { useDate } from "./SectionTiming.hooks";

export function buildCallTimelineLink(callId: string) {
  // Trailing slash inside DASHBOARD_URL.
  return `${DASHBOARD_URL}calls/${callId}/timeline`;
}

function GridEntry({ label, children }: PropsWithChildren<{ label: string }>) {
  return (
    <>
      <span className="text-sm font-medium text-muted-foreground">{label}</span>
      <div>{children}</div>
    </>
  );
}

export function SectionTiming() {
  const { t } = useTranslation();
  const { call } = useCall();
  const { displayRelativeDay } = useDate({
    targetDate: new Date(call?.createdAt ?? ""),
  });

  const timing = useMemo(() => {
    if (call?.answeredAt && call?.durationInSecond) {
      const waitedTime = differenceInSeconds(
        new Date(call.answeredAt),
        new Date(call.createdAt)
      );

      const inCallTime = call.durationInSecond - waitedTime;

      return { waitedTime, inCallTime };
    }
  }, [call?.answeredAt, call?.createdAt, call?.durationInSecond]);

  if (!call) {
    return null;
  }

  return (
    <Section data-test="timing-section">
      <SectionHeader>
        <SectionTitle>{t("call_context.timing")}</SectionTitle>
      </SectionHeader>
      <div className="flex flex-col gap-2">
        <div
          className="grid items-center gap-2"
          style={{ gridTemplateColumns: "auto 1fr" }}
        >
          <GridEntry label={t("call_context.start_time")}>
            <div className="flex items-center gap-1">
              <span>
                {`${longPrettifiedDate(new Date(call.createdAt), { locale: i18n.language })}`}
              </span>
              {displayRelativeDay && (
                <span className="text-muted-foreground">
                  {`(${getRelativeDate(new Date(call.createdAt), i18n.language)})`}
                </span>
              )}
            </div>
          </GridEntry>
          {call.answeredAt &&
            timing?.waitedTime &&
            call.type !== "voicemail" && (
              <GridEntry label={t("call_context.waited_time")}>
                <Duration seconds={timing.waitedTime} />
              </GridEntry>
            )}
          {call.durationInSecond &&
            timing?.inCallTime &&
            call.type !== "voicemail" && (
              <GridEntry label={t("call_context.in_call_time")}>
                <Duration seconds={timing.inCallTime} />
              </GridEntry>
            )}
          {call.durationInSecond &&
            call.answeredAt &&
            call.type !== "voicemail" && (
              <GridEntry label={t("call_context.total_call_time")}>
                <Duration seconds={call.durationInSecond} />
              </GridEntry>
            )}
        </div>
        {call.direction === "inbound" && (
          <a
            data-test="open-call-timeline-anchor"
            className="inline-flex items-center gap-1 self-start p-0 text-sm font-normal text-primary"
            target="_blank"
            href={buildCallTimelineLink(call.id)}
            rel="noreferrer"
          >
            {t("open_call_timeline")}
            <ExternalLink className="size-[18px]" />
          </a>
        )}
      </div>
    </Section>
  );
}
